import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import JsonLd from '@/components/feature/JsonLd';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ReadingProgress from '@/components/feature/ReadingProgress';
import { stories } from '@/mocks/stories';
import { siteInfo } from '@/mocks/categories';
import ArticleBody from './components/ArticleBody';
import ArticleToc from './components/ArticleToc';
import RelatedStories from './components/RelatedStories';

export default function StoryDetail() {
  const { id } = useParams<{ id: string }>();
  const story = stories.find((s) => s.id === id);
  const [activeId, setActiveId] = useState('');
  const [copied, setCopied] = useState(false);

  const headings = useMemo(
    () => (story ? story.content.filter((b) => b.type === 'h3').map((b, i) => ({ id: `section-${i + 1}`, text: b.text })) : []),
    [story],
  );

  usePageMeta({
    title: story ? `${story.title} | 瓜田笔记` : '趣闻未找到 | 瓜田笔记',
    description: story ? story.excerpt : '这篇高校吃瓜故事暂时找不到了，去全部趣闻看看吧。',
    keywords: story ? story.tags.join(',') : '高校吃瓜,校园故事',
    path: `/stories/${id ?? ''}`,
  });

  useEffect(() => {
    if (headings.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: '-96px 0px -70% 0px', threshold: 0 },
    );
    headings.forEach((h) => {
      const el = document.getElementById(h.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [headings]);

  if (!story) {
    return (
      <section className="mx-auto flex w-full max-w-content flex-col items-center justify-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-emotion-sad-line text-3xl" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">这篇瓜好像被吃完了</h1>
        <p className="mt-3 max-w-md text-sm text-foreground-600">
          你访问的故事不存在或已经下架，回到全部趣闻看看其他校园故事吧。
        </p>
        <Link
          to="/stories"
          className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          返回全部趣闻
        </Link>
      </section>
    );
  }

  const related = stories.filter((s) => s.categorySlug === story.categorySlug && s.id !== story.id).slice(0, 3);
  const fallbackRelated = stories.filter((s) => s.id !== story.id).slice(0, 3);

  const index = stories.findIndex((s) => s.id === story.id);
  const prev = index > 0 ? stories[index - 1] : null;
  const next = index < stories.length - 1 ? stories[index + 1] : null;

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: story.title,
      description: story.excerpt,
      image: story.cover,
      datePublished: story.date,
      dateModified: story.date,
      inLanguage: 'zh-CN',
      articleSection: story.categoryName,
      keywords: story.tags.join(','),
      author: { '@type': 'Person', name: story.author },
      publisher: { '@type': 'Organization', name: siteInfo.name },
      mainEntityOfPage: `${window.location.origin}/stories/${story.id}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: `${window.location.origin}/` },
        { '@type': 'ListItem', position: 2, name: '全部趣闻', item: `${window.location.origin}/stories` },
        { '@type': 'ListItem', position: 3, name: story.title, item: `${window.location.origin}/stories/${story.id}` },
      ],
    },
  ];

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <>
      <JsonLd data={structuredData} />
      <ReadingProgress />

      <article>
        <header className="border-b border-background-200 bg-background-100/50">
          <div className="mx-auto w-full max-w-content px-4 py-10 md:px-6 md:py-12">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '全部趣闻', to: '/stories' },
                { label: story.categoryName, to: `/stories?category=${story.categorySlug}` },
                { label: story.title },
              ]}
            />

            <div className="mt-6 max-w-3xl">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
                <i className="ri-price-tag-3-line" />
                {story.categoryName}
              </span>
              <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
                {story.title}
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">{story.excerpt}</p>

              <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-foreground-500">
                <span className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 font-heading text-xs font-bold text-primary-700">
                    {story.author.slice(-2)}
                  </span>
                  {story.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line" /> {story.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-timer-line" /> {story.readTime} 分钟
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line" /> {(story.views / 10000).toFixed(1)} 万围观
                </span>
                <button
                  type="button"
                  onClick={handleShare}
                  className="flex items-center gap-1.5 rounded-md border border-background-300 px-2.5 py-1.5 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                >
                  <i className={copied ? 'ri-check-line' : 'ri-share-line'} />
                  {copied ? '链接已复制' : '分享这篇'}
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="mx-auto w-full max-w-content px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <div className="relative aspect-[16/9] w-full overflow-hidden rounded-lg">
                <img
                  src={story.cover}
                  alt={`${story.title} - ${story.categoryName}配图`}
                  className="h-full w-full object-cover object-top"
                />
              </div>

              <ArticleBody blocks={story.content} headingIds={headings.map((h) => h.id)} />

              <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
                <span className="text-xs text-foreground-500">相关话题</span>
                {story.tags.map((tag) => (
                  <Link
                    key={tag}
                    to={`/stories?q=${encodeURIComponent(tag)}`}
                    className="rounded-full bg-background-100 px-3 py-1.5 text-xs text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-800"
                  >
                    #{tag}
                  </Link>
                ))}
              </div>

              <nav className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2" aria-label="上下篇导航">
                {prev ? (
                  <Link
                    to={`/stories/${prev.id}`}
                    className="group rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300"
                  >
                    <span className="flex items-center gap-1 text-xs text-foreground-500">
                      <i className="ri-arrow-left-line" /> 上一篇
                    </span>
                    <p className="mt-2 line-clamp-2 font-heading text-sm font-bold text-foreground-900 group-hover:text-primary-600">
                      {prev.title}
                    </p>
                  </Link>
                ) : (
                  <span />
                )}
                {next && (
                  <Link
                    to={`/stories/${next.id}`}
                    className="group rounded-lg border border-background-200 bg-background-50 p-4 text-right transition-colors hover:border-primary-300"
                  >
                    <span className="flex items-center justify-end gap-1 text-xs text-foreground-500">
                      下一篇 <i className="ri-arrow-right-line" />
                    </span>
                    <p className="mt-2 line-clamp-2 font-heading text-sm font-bold text-foreground-900 group-hover:text-primary-600">
                      {next.title}
                    </p>
                  </Link>
                )}
              </nav>
            </div>

            <aside className="lg:col-span-4">
              <div className="space-y-6 lg:sticky lg:top-24">
                <ArticleToc items={headings} activeId={activeId} />

                <div className="rounded-lg border border-background-200 bg-accent-50 p-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className="ri-quill-pen-line text-lg" />
                  </span>
                  <h2 className="mt-3 font-heading text-base font-bold text-foreground-950">你也有同款经历？</h2>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    把你的校园故事发给我们，说不定下一篇就是它。
                  </p>
                  <a
                    href={`mailto:${siteInfo.submitEmail}`}
                    className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600"
                  >
                    <i className="ri-mail-send-line" /> 投个稿
                  </a>
                </div>

                <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h2 className="font-heading text-base font-bold text-foreground-950">关于瓜田笔记</h2>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                    我们只写那些真实、轻松、带点温度的校园瞬间，所有人物与高校信息都做过模糊化处理。
                  </p>
                  <Link
                    to="/about"
                    className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary-600 transition-colors hover:text-primary-700"
                  >
                    了解我们的内容原则 <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>

      <RelatedStories stories={related.length > 0 ? related : fallbackRelated} />
    </>
  );
}